import { flexRender } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import { FOCUS_RING } from '@/shared/components/pillClasses';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

import { SORT_NATURAL, type SortDir, type SortKey } from '../listView';
import {
  TASK_GRID_CLASS,
  TASK_GRID_STYLE,
  cellClass,
  type useTaskTable,
} from '../taskColumns';

/** Fixed, and exported: the page offsets the list beneath it by exactly this. */
export const TASK_LIST_HEADER_HEIGHT = 32;

export interface TaskListHeaderProps {
  table: ReturnType<typeof useTaskTable>;
  sort: { key: SortKey; dir: SortDir } | undefined;
  /** `undefined` restores the default order. */
  onSortChange: (next: { key: SortKey; dir: SortDir } | undefined) => void;
}

/**
 * The column names above the list, and the way to order it (spec 112 US3,
 * superseding 068 FR-006 and 110 FR-017).
 *
 * **A group of toggle buttons, not table headers.** Every row is one link —
 * the whole row is the target — and a link cannot also be a table row, so
 * there is no table for `aria-sort` to live in. A labelled group of pressed /
 * not-pressed buttons says the same thing honestly: which column the list is
 * ordered by, and that pressing a heading changes it. Labels is not an order
 * and stays a plain, visual label.
 *
 * A column starts on its natural direction — names A–Z, state most urgent
 * first, figures and times largest or newest first — and pressing the active
 * one reverses it. Choosing Updated, newest first, is the default order, so it
 * clears the order rather than naming it.
 *
 * Sticky, so a column can still be named halfway down a long list.
 *
 * Since spec 113 every ordering heading carries its arrow — faint on hover and
 * focus until pressed — so it can be told from a label beforehand, and "Reset
 * order" lives here, beside the headings it resets, instead of on the count
 * line.
 */
export function TaskListHeader({
  table,
  sort,
  onSortChange,
}: TaskListHeaderProps) {
  // A phone's rows are two lines, not the table's tracks (spec 265), so the
  // headings stop pretending to sit above columns and read as a row of orders.
  const phone = usePhoneWidth();
  const press = (key: SortKey) => {
    const dir: SortDir =
      sort?.key === key
        ? sort.dir === 'asc'
          ? 'desc'
          : 'asc'
        : SORT_NATURAL[key];
    onSortChange(
      key === 'updated' && dir === SORT_NATURAL.updated
        ? undefined
        : { key, dir },
    );
  };

  // Sentence case, no letter-spacing (spec 127 FR-010): uppercase with
  // tracking widened "Created" and "Updated" past their 88px tracks, so they
  // read "CREAT…" and "UPDAT…".
  return (
    <div
      role="group"
      aria-label="Order by"
      data-testid="task-list-header"
      style={
        phone
          ? { height: TASK_LIST_HEADER_HEIGHT }
          : { ...TASK_GRID_STYLE, height: TASK_LIST_HEADER_HEIGHT }
      }
      className={`${phone ? 'flex gap-4 px-4' : TASK_GRID_CLASS} bg-background border-border text-muted-foreground sticky top-0 z-10 items-center border-b text-xs font-medium`}
    >
      {table.getHeaderGroups()[0]?.headers.map((header) => {
        const meta = header.column.columnDef.meta;
        const name = flexRender(
          header.column.columnDef.header,
          header.getContext(),
        );
        const key = meta?.sort;
        if (!key) {
          return (
            <div key={header.id} aria-hidden="true" className={cellClass(meta)}>
              <span className="block truncate">{name}</span>
            </div>
          );
        }
        const active = sort?.key === key;
        return (
          <div
            key={header.id}
            className={`${cellClass(meta)} ${key === 'task' ? 'flex items-center gap-2' : ''}`}
          >
            <button
              type="button"
              aria-pressed={active}
              aria-label={`Order by ${String(header.column.columnDef.header)}`}
              onClick={() => {
                press(key);
              }}
              className={`group hover:text-foreground touch-target inline-flex max-w-full items-center gap-1 rounded-sm ${FOCUS_RING} ${
                active ? 'text-foreground' : ''
              } ${meta.align === 'end' ? 'ml-auto' : ''}`}
            >
              <span className="truncate">{name}</span>
              {/*
                Always there, so a sortable heading can be told from a label
                before it is pressed (spec 113 FR-015): faint on hover and
                focus, pointing the way a first press would order the list;
                solid on the active heading. Its width is always reserved, so
                nothing shifts when it appears.
              */}
              <span
                aria-hidden="true"
                className={
                  active
                    ? ''
                    : 'opacity-0 transition-opacity group-hover:opacity-50 group-focus-visible:opacity-50'
                }
              >
                {(active ? sort.dir : SORT_NATURAL[key]) === 'asc' ? '↑' : '↓'}
              </span>
            </button>
            {/*
              One step back to newest-changed first (spec 112 FR-014), moved
              here from the count line by spec 113 FR-016, so it sits with the
              headings it resets. In the Task heading's cell — the widest, with
              a short heading — because a track of its own would shift every
              column, and pinned to the row's end it would cover Updated.
            */}
            {key === 'task' && sort ? (
              <Button
                variant="ghost"
                size="xs"
                className="font-normal tracking-normal normal-case"
                onClick={() => {
                  onSortChange(undefined);
                }}
              >
                Reset order
              </Button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
